import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useToast } from '@/hooks/use-toast';
import { uploadAvatar, deleteAvatar } from '@/lib/supabase/storage';
import { updateUserProfile } from '@/lib/supabase/user';
import { Loader2 } from 'lucide-react';

interface AvatarUploadProps {
  avatarUrl?: string | null;
  name: string;
  onSuccess?: (url: string) => void;
}

export function AvatarUpload({ avatarUrl, name, onSuccess }: AvatarUploadProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [currentAvatarUrl, setCurrentAvatarUrl] = useState(avatarUrl);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Error',
        description: 'Please select an image file',
        variant: 'destructive',
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'Error',
        description: 'Image must be less than 5MB',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);
    try {
      // Delete old avatar if exists
      if (currentAvatarUrl) {
        try {
          await deleteAvatar(currentAvatarUrl);
        } catch {
          // Continue even if deletion fails - old file will remain orphaned
        }
      }

      const url = await uploadAvatar(file);
      await updateUserProfile({ avatar_url: url });

      // Update local state immediately for better UX
      setCurrentAvatarUrl(url);

      onSuccess?.(url);
      toast({
        title: 'Success',
        description: 'Avatar updated successfully',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update avatar',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
      // Reset input so same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const getInitials = (displayName: string): string => {
    const parts = displayName.trim().split(' ').filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0][0]?.toUpperCase() || '?';
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  return (
    <div className="flex items-center gap-6">
      <Avatar className="h-20 w-20">
        <AvatarImage src={currentAvatarUrl || undefined} key={currentAvatarUrl} />
        <AvatarFallback className="text-lg">
          {getInitials(name)}
        </AvatarFallback>
      </Avatar>
      <div className="flex flex-col gap-2">
        <Button
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={isLoading}
        >
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Change Avatar
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
        />
        <p className="text-xs text-muted-foreground">
          Recommended: Square image, max 5MB
        </p>
      </div>
    </div>
  );
}
